import {DI_TOKENS} from '@/di/di.tokens';
import {ErrorCode} from '@/services/ErrorService/ErrorService.constants';

import {injected} from 'brandi';

import {HttpError, HttpErrorBackend, HttpErrorProject, HttpStatus, uuid4} from '@specials/project-user';

import {ErrorResourcesBase, ErrorResourcesContent, ResourcesAll} from '@/resources';
import {FrontendError} from '@/services/ErrorService/FrontendError';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {PlatformService} from '@/services/PlatformService/PlatformService';

export interface ErrorBreadcrumbs {
  errorCode?: string;
  httpCode?: HttpStatus;
  requestId?: string;
  error?: Error;
  errorMessage?: string;
  timestamp?: string;
}

export interface ErrorContent {
  resources: ErrorResourcesContent;
  breadcrumbs: ErrorBreadcrumbs;
}

export class ErrorService {
  constructor(
    private readonly resources: ResourcesAll,
    private readonly platformService: PlatformService,
    private readonly loggerService: LoggerService,
  ) {}

  public buildErrorData = (error: FrontendError | HttpError | unknown): ErrorContent => {
    const breadcrumbs = this.extractBreadcrumbs(error);
    const isMb = this.platformService.isMb();
    const matchedResources = this.extractResources(breadcrumbs);
    const platformResources = matchedResources ?? this.resources.errors.fallback;
    const resources = isMb ? platformResources.mb : platformResources.web;

    // Отдельно логируем в ErrorHub необработанную ошибку, потому что разработчик
    // забыл добавить для ошибки ресурсы и пользователь стандартный экран
    // "Что-то пошло не так", вместо конкретного описания ошибки
    if (typeof matchedResources === 'undefined') {
      this.loggerService.logUnMatchedError(breadcrumbs);
    }

    return {
      resources,
      breadcrumbs,
    };
  };

  private extractBreadcrumbs = (error: FrontendError | HttpError | unknown): ErrorBreadcrumbs => {
    if (HttpErrorProject.isHttpErrorProject(error)) {
      return {
        httpCode: error.httpCode,
        errorCode: error.errorCode || ErrorCode.UndefinedError,
        requestId: error.requestId,
        timestamp: error.timestamp,
      };
    }

    if (HttpErrorBackend.isHttpErrorBackend(error)) {
      return {
        httpCode: error.response.httpCode,
        errorCode: ErrorCode.UnknownError,
        requestId: uuid4(),
        timestamp: new Date().toISOString(),
      };
    }

    if (FrontendError.isFrontendError(error)) {
      return {
        errorCode: error.errorCode || ErrorCode.UndefinedError,
        errorMessage: error.message,
        requestId: error.requestId,
        timestamp: error.timestamp,
        error: error.error,
      };
    }

    return {
      errorCode: ErrorCode.UnmatchedError,
      requestId: uuid4(),
      timestamp: new Date().toISOString(),
    };
  };

  private extractResources = (breadcrumbs: ErrorBreadcrumbs): ErrorResourcesBase | undefined => {
    const {errors} = this.resources;

    const {errorCode, httpCode} = breadcrumbs;

    if (errorCode) {
      const resources = errors.errorCodes.find((e) => e.errorCodes.includes(errorCode));
      if (resources) return resources;
    }

    if (httpCode) {
      const resources = errors.httpCodes.find((e) => e.httpCodes.includes(httpCode));
      if (resources) return resources;
    }
  };
}

injected(ErrorService, DI_TOKENS.resources, DI_TOKENS.platformService, DI_TOKENS.loggerService);
